/**
 * Sponsorship tiers + entitlements engine — the single source of truth for
 * "who paid for what" and "who appears where" (Build Spec §2.1–2.3).
 *
 * Placement is a THREE-GATE decision (see appearsOn):
 *   1. sponsor is active
 *   2. tier is eligible for the surface (TIER_ENTITLEMENTS + per-sponsor override)
 *   3. the admin Placement Control policy has that tier enabled for the surface
 */

export type SponsorTier =
  | 'platinum-white-glove'
  | 'platinum-plus'
  | 'platinum'
  | 'platinum-select'
  | 'gold'
  | 'silver'
  | 'bronze'
  | 'custom';

/** Where a sponsor can appear. */
export type Surface =
  | 'homepageHero'
  | 'homepageCarousel'
  | 'sponsorPage'
  | 'ticketEmailFooter'
  | 'receiptEmailFooter'
  | 'featuredSlot'
  | 'blogSlot'
  | 'jersey'
  | 'rinkPA';

export type EntitlementSet = Record<Surface, boolean>;

export const SURFACES: Surface[] = [
  'homepageHero', 'homepageCarousel', 'sponsorPage',
  'ticketEmailFooter', 'receiptEmailFooter',
  'featuredSlot', 'blogSlot', 'jersey', 'rinkPA',
];

/** Human labels for each surface (admin placement preview + Placement Control). */
export const SURFACE_LABELS: Record<Surface, string> = {
  homepageHero: 'Homepage hero',
  homepageCarousel: 'Homepage carousel',
  sponsorPage: 'Sponsor page',
  ticketEmailFooter: 'Ticket emails',
  receiptEmailFooter: 'Receipt emails',
  featuredSlot: 'Featured sections',
  blogSlot: 'Blog / news',
  jersey: 'Jersey / helmet',
  rinkPA: 'Rink & PA',
};

export interface TierMeta {
  label: string;
  /** Cash price in USD; null for the custom/hybrid tier. */
  price: number | null;
  /** 0 = highest tier. Drives sort + rotation weighting. */
  rank: number;
}

export const TIER_META: Record<SponsorTier, TierMeta> = {
  'platinum-white-glove': { label: 'Platinum White Glove', price: 20000, rank: 0 },
  'platinum-plus':        { label: 'Platinum Plus',        price: 15000, rank: 1 },
  'platinum':             { label: 'Platinum',             price: 10000, rank: 2 },
  'platinum-select':      { label: 'Platinum Select',      price: 8500,  rank: 3 },
  'gold':                 { label: 'Gold',                 price: 7500,  rank: 4 },
  'silver':               { label: 'Silver',               price: 5000,  rank: 5 },
  'bronze':               { label: 'Bronze',               price: 2500,  rank: 6 },
  'custom':               { label: 'Custom / Hybrid',      price: null,  rank: 7 },
};

/** Ordered tier keys (top → bottom) for pickers and catalogs. */
export const TIER_ORDER: SponsorTier[] = [
  'platinum-white-glove', 'platinum-plus', 'platinum', 'platinum-select', 'gold', 'silver', 'bronze', 'custom',
];

/**
 * PROVISIONAL legacy mapping — used ONLY so pre-migration sponsor documents
 * (old enum: premier/gold/silver/bronze/partner) still display sensibly. The
 * real data migration mapping requires owner sign-off (Build Spec §2.6).
 */
export const LEGACY_TIER_MAP: Record<string, SponsorTier> = {
  premier: 'platinum-plus',
  gold: 'gold',
  silver: 'silver',
  bronze: 'bronze',
  partner: 'bronze',
};

/** Coerce any stored tier value (incl. legacy/unknown) to a valid SponsorTier. */
export function normalizeTier(raw: string | undefined | null): SponsorTier {
  if (raw && raw in TIER_META) return raw as SponsorTier;
  if (raw && LEGACY_TIER_MAP[raw]) return LEGACY_TIER_MAP[raw];
  return 'bronze';
}

export function tierLabel(raw: string): string {
  return TIER_META[normalizeTier(raw)].label;
}

export function tierRank(raw: string): number {
  return TIER_META[normalizeTier(raw)].rank;
}

/** What each tier is ELIGIBLE for (the max; admin policy narrows it). */
export const TIER_ENTITLEMENTS: Record<SponsorTier, EntitlementSet> = {
  'platinum-white-glove': { homepageHero: true,  homepageCarousel: true, sponsorPage: true, ticketEmailFooter: true,  receiptEmailFooter: true,  featuredSlot: true,  blogSlot: true,  jersey: true,  rinkPA: true },
  'platinum-plus':        { homepageHero: true,  homepageCarousel: true, sponsorPage: true, ticketEmailFooter: true,  receiptEmailFooter: true,  featuredSlot: true,  blogSlot: true,  jersey: true,  rinkPA: true },
  'platinum':             { homepageHero: false, homepageCarousel: true, sponsorPage: true, ticketEmailFooter: true,  receiptEmailFooter: false, featuredSlot: true,  blogSlot: true,  jersey: true,  rinkPA: true },
  'platinum-select':      { homepageHero: false, homepageCarousel: true, sponsorPage: true, ticketEmailFooter: true,  receiptEmailFooter: false, featuredSlot: true,  blogSlot: true,  jersey: true,  rinkPA: true },
  'gold':                 { homepageHero: false, homepageCarousel: true, sponsorPage: true, ticketEmailFooter: false, receiptEmailFooter: false, featuredSlot: true,  blogSlot: true,  jersey: true,  rinkPA: true },
  'silver':               { homepageHero: false, homepageCarousel: true, sponsorPage: true, ticketEmailFooter: false, receiptEmailFooter: false, featuredSlot: false, blogSlot: false, jersey: false, rinkPA: true },
  'bronze':               { homepageHero: false, homepageCarousel: true, sponsorPage: true, ticketEmailFooter: false, receiptEmailFooter: false, featuredSlot: false, blogSlot: false, jersey: false, rinkPA: true },
  'custom':               { homepageHero: false, homepageCarousel: true, sponsorPage: true, ticketEmailFooter: true,  receiptEmailFooter: false, featuredSlot: true,  blogSlot: true,  jersey: true,  rinkPA: true },
};

// --- Admin Placement Control policy (settings/sponsorPlacements) ---
export interface PlacementPolicyRow {
  enabledTiers: SponsorTier[];
  maxSlots: number | 'all';
  rotation: 'weighted' | 'even';
}
export type PlacementPolicy = Partial<Record<Surface, PlacementPolicyRow>>;

/** Minimal shape needed to resolve placements (Sponsor satisfies this). */
export interface PlaceableSponsor {
  tier: SponsorTier | string;
  active: boolean;
  placementsOverride?: Partial<EntitlementSet>;
}

/** Gate 2: tier eligibility (with per-sponsor override). */
export function isEligible(sponsor: PlaceableSponsor, surface: Surface): boolean {
  const override = sponsor.placementsOverride?.[surface];
  if (typeof override === 'boolean') return override;
  return TIER_ENTITLEMENTS[normalizeTier(sponsor.tier)][surface];
}

/** Full three-gate: does this sponsor appear on this surface, given policy? */
export function appearsOn(sponsor: PlaceableSponsor, surface: Surface, policy?: PlacementPolicy): boolean {
  if (!sponsor.active) return false;
  if (!isEligible(sponsor, surface)) return false;
  const row = policy?.[surface];
  if (row && !row.enabledTiers.includes(normalizeTier(sponsor.tier))) return false;
  return true;
}

/** Resolve the full per-surface placement set for a sponsor. */
export function resolvePlacements(sponsor: PlaceableSponsor, policy?: PlacementPolicy): EntitlementSet {
  const out = {} as EntitlementSet;
  for (const surface of SURFACES) {
    out[surface] = appearsOn(sponsor, surface, policy);
  }
  return out;
}

const ALL_TIERS: SponsorTier[] = [
  'platinum-white-glove', 'platinum-plus', 'platinum', 'platinum-select', 'gold', 'silver', 'bronze', 'custom',
];

/** Default admin Placement Control policy — seeds settings/sponsorPlacements (Build Spec §3.3). */
export const DEFAULT_PLACEMENT_POLICY: PlacementPolicy = {
  homepageHero:       { enabledTiers: ['platinum-white-glove', 'platinum-plus'], maxSlots: 2, rotation: 'weighted' },
  homepageCarousel:   { enabledTiers: ALL_TIERS, maxSlots: 'all', rotation: 'weighted' },
  sponsorPage:        { enabledTiers: ALL_TIERS, maxSlots: 'all', rotation: 'weighted' },
  ticketEmailFooter:  { enabledTiers: ['platinum-white-glove', 'platinum-plus', 'platinum', 'platinum-select'], maxSlots: 3, rotation: 'weighted' },
  receiptEmailFooter: { enabledTiers: ['platinum-white-glove', 'platinum-plus'], maxSlots: 2, rotation: 'weighted' },
  featuredSlot:       { enabledTiers: ['platinum-white-glove', 'platinum-plus', 'platinum', 'platinum-select', 'gold'], maxSlots: 4, rotation: 'weighted' },
  blogSlot:           { enabledTiers: ['platinum-white-glove', 'platinum-plus', 'platinum', 'platinum-select', 'gold'], maxSlots: 3, rotation: 'weighted' },
};

/** Select the sponsors that appear on a surface — filtered, weighted by tier, capped by maxSlots. */
export function selectForSurface<T extends PlaceableSponsor & { displayOrder?: number }>(
  sponsors: T[],
  surface: Surface,
  policy?: PlacementPolicy
): T[] {
  const eligible = sponsors.filter(s => appearsOn(s, surface, policy));
  eligible.sort((a, b) => {
    const ra = tierRank(a.tier as string);
    const rb = tierRank(b.tier as string);
    if (ra !== rb) return ra - rb;
    return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
  });
  const max = policy?.[surface]?.maxSlots;
  return typeof max === 'number' ? eligible.slice(0, max) : eligible;
}
